import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import OfficeEditor, { officeKind } from '../components/office/OfficeEditor';
import { createBridge } from '../components/office/bridge';
import { runEditorAction } from '../components/office/editorActions';
import { ejecutar as ejecutarComando, escucharEstado } from '../components/office/editorComandos';
import { askAuroraAI, errorMessage, insertAIResult, readEditorContext } from '../components/office/officeAI';
import { applyAppLayout, applyEditorTheme, rememberEditorTheme } from '../components/office/skin';
import { usePresencia } from '../components/office/presencia';
import '../components/office/office.css';

/*
 * Editor de Orvian Office dentro de la app del teléfono (Orvian Email, Flutter).
 *
 * La app dibuja en nativo todo lo de alrededor (encabezado, Aurora IA, compartir, selector de Drive) y
 * carga esta página en una vista web del MISMO dominio que el motor: sin eso no funcionan ni la piel
 * ni el plugin puente. Aquí solo vive el editor, y la lógica es la del portal (OfficeEditor, officeAI,
 * editorActions): la app no la duplica, la llama.
 *
 * Protocolo (canal JavaScript `OrvianApp`, mensajes JSON):
 *   app → página   window.orvianHost.call(id, cmd, args)   →  { type: 'result', id, ok, result }
 *   página → app   { type: 'config' | 'ready' | 'saving' | 'unavailable' | 'pick' | 'toast' | 'reloading' }
 * Comandos: setToken, setTheme, editor, toolbar, action, aiRun, aiChat, aiInsert,
 *            resolvePick, reload. Eventos: … y `format` (estado de la selección).
 * Contrato espejo en app/lib/core/office_host.dart: si cambia uno, cambia el otro.
 *
 * Sesión: la app pasa el access token en el fragmento (#t=, no viaja al servidor) y lo renueva con
 * setToken; nunca se le da el refresh a la vista web.
 */

const DOC_KIND = { word: 'doc', cell: 'sheet', slide: 'slides', pdf: 'pdf', diagram: 'diagram' };

function send(message) {
  try {
    window.OrvianApp?.postMessage(JSON.stringify(message));
  } catch {
    /* sin app (abierta en un navegador): no hay a quién avisar */
  }
}

function readSessionFromUrl() {
  const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  const token = hash.get('t');
  if (token) {
    localStorage.setItem('zuno_access', token);
    // El token no debe quedarse en la barra ni en el historial de la vista web
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
  const query = new URLSearchParams(window.location.search);
  return { dark: query.get('theme') === 'dark' };
}

export default function AppOffice() {
  const { id: fileId } = useParams();
  const [initial] = useState(readSessionFromUrl);
  const [dark, setDark] = useState(initial.dark);
  const [nonce, setNonce] = useState(0);
  const [ready, setReady] = useState(false);
  const iframeRef = useRef(null);
  const bridgeRef = useRef(null);
  const kindRef = useRef('doc');
  const picks = useRef(new Map());
  const dejarDeEscuchar = useRef(null);
  const darkRef = useRef(dark);
  darkRef.current = dark;

  if (!bridgeRef.current) bridgeRef.current = createBridge();
  // Quien edita desde la app del teléfono también aparece en los avatares del portal (aquí no se dibujan)
  usePresencia({ fileId, activo: !!fileId });
  useEffect(() => () => {
    bridgeRef.current?.dispose();
    dejarDeEscuchar.current?.();
  }, []);

  // Los avisos del editor (errores al guardar, restaurar, avisar a mencionados) salen en la app, no en la web
  useEffect(() => {
    const original = { success: toast.success, error: toast.error, message: toast.message };
    const forward = (level) => (text) => send({ type: 'toast', level, message: String(text || '') });
    toast.success = forward('success');
    toast.error = forward('error');
    toast.message = forward('info');
    document.documentElement.classList.add('app-office');
    return () => {
      Object.assign(toast, original);
      document.documentElement.classList.remove('app-office');
    };
  }, []);

  // Tema en caliente (sin recargar: recargar desconectaría de la coedición)
  useEffect(() => {
    rememberEditorTheme(dark);
    if (!ready) return undefined;
    let tries = 0;
    let timer = null;
    const apply = () => {
      if (applyEditorTheme(iframeRef.current, darkRef.current) || tries >= 20) return;
      tries += 1;
      timer = setTimeout(apply, 250);
    };
    apply();
    return () => clearTimeout(timer);
  }, [dark, ready]);

  const commands = useCallback(() => ({
    setToken: ({ token }) => {
      if (token) localStorage.setItem('zuno_access', token);
      return true;
    },
    setTheme: ({ dark: next }) => {
      setDark(!!next);
      return true;
    },
    // Recién abierto, la barra del motor tarda un poco más que `onAppReady`: se reintenta unos segundos
    // Edición desde la barra NATIVA de la app: son las mismas llamadas que hace
    // la barra del motor, que en el teléfono está oculta por estrecha.
    editor: ({ cmd, value }) => ejecutarComando(iframeRef.current, cmd, value),
    // Escotilla: enseñar la barra completa del motor para lo que la app no cubre
    toolbar: ({ visible }) => {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return false;
      doc.documentElement.classList.toggle('orvian-barra', !!visible);
      // El motor mide el lienzo al cambiar de tamaño
      iframeRef.current?.contentWindow?.dispatchEvent(new Event('resize'));
      return true;
    },
    action: async ({ name }) => {
      for (let i = 0; i < 8; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        if (await runEditorAction(iframeRef.current, name)) return true;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => { setTimeout(resolve, 400); });
      }
      return false;
    },
    // Misma lógica que la barra "Escribir con Aurora IA" del portal (AIComposer.run)
    aiRun: async ({ action, prompt = '', needsSelection = false }) => {
      const kind = kindRef.current;
      const ctx = await readEditorContext(bridgeRef.current, kind);
      if (needsSelection && !ctx.selection) return { needsSelection: true };
      const finalAction = kind === 'doc' && action === 'write' && ctx.selection ? 'rewrite' : action;
      const res = await askAuroraAI({
        file_id: fileId,
        kind,
        action: finalAction,
        prompt,
        selection: ctx.selection || '',
        context: ctx.context || '',
        current: ctx.current || '',
      });
      return { ...res, deck: finalAction === 'deck', hadSelection: !!ctx.selection, target: ctx.target || null };
    },
    // Misma lógica que el panel de Aurora IA del portal (AIPanel.send)
    aiChat: async ({ messages }) => {
      const kind = kindRef.current;
      const ctx = await readEditorContext(bridgeRef.current, kind).catch(() => ({ context: '' }));
      const res = await askAuroraAI({
        file_id: fileId,
        kind,
        action: 'chat',
        messages,
        context: ctx.context || '',
        selection: ctx.selection || '',
      });
      return { text: res.text };
    },
    aiInsert: ({ result, mode = 'cursor', asTable = false }) => (
      insertAIResult(bridgeRef.current, { ...result, asTable }, mode)
    ),
    resolvePick: ({ reqId, choice }) => {
      const resolve = picks.current.get(reqId);
      picks.current.delete(reqId);
      resolve?.(choice || null);
      return true;
    },
    reload: () => {
      setReady(false);
      setNonce((n) => n + 1);
      return true;
    },
  }), [fileId]);

  useEffect(() => {
    const table = commands();
    window.orvianHost = {
      call: async (id, cmd, args) => {
        try {
          const handler = table[cmd];
          if (!handler) throw new Error(`Comando desconocido: ${cmd}`);
          const result = await handler(args || {});
          send({ type: 'result', id, ok: true, result: result === undefined ? null : result });
        } catch (err) {
          send({ type: 'result', id, ok: false, result: errorMessage(err, 'No se pudo completar') });
        }
      },
    };
    return () => { delete window.orvianHost; };
  }, [commands]);

  // El selector de archivos del editor lo pinta la app: aquí se espera su respuesta
  const pickFromApp = useCallback((mode) => new Promise((resolve) => {
    const reqId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    picks.current.set(reqId, resolve);
    send({ type: 'pick', reqId, mode });
  }), []);

  return (
    <div className={`zo-app-host ${dark ? 'is-dark' : ''}`} data-testid="app-office">
      <OfficeEditor
        key={nonce}
        fileId={fileId}
        dark={dark}
        layout="app"
        onConfig={(cfg) => {
          const name = cfg?.config?.document?.title || '';
          kindRef.current = officeKind(name, DOC_KIND[cfg.document_type] || 'doc');
          send({
            type: 'config',
            role: cfg.my_role,
            canEdit: !!cfg.can_edit,
            whiteLabel: !!cfg.white_label,
            documentType: cfg.document_type,
            kind: kindRef.current,
            name,
          });
        }}
        onReady={(iframe) => {
          iframeRef.current = iframe;
          setReady(true);
          // Con las tiras laterales ocultas el motor tiene que volver a medir el lienzo
          applyAppLayout(iframe);
          setTimeout(() => applyAppLayout(iframe), 400);
          dejarDeEscuchar.current?.();
          dejarDeEscuchar.current = escucharEstado(iframe, (estado) => send({ type: 'format', ...estado }));
          send({ type: 'ready' });
        }}
        onStateChange={(saving) => send({ type: 'saving', value: !!saving })}
        onUnavailable={(detail) => send({ type: 'unavailable', detail })}
        onReload={() => {
          send({ type: 'reloading' });
          setReady(false);
          setNonce((n) => n + 1);
        }}
        onPick={pickFromApp}
      />
    </div>
  );
}
